import { Gauge, Fuel, AlertTriangle, Quote } from "lucide-react";

// Plain-English explainer for ELD & IFTA (guide Step/Mission 6).
export default function EldIftaExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="eld-ifta-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Gauge className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">ELD &amp; IFTA — the two systems</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        Two different systems that both start on day one: an <strong>ELD</strong> tracks your driving hours
        automatically, and <strong>IFTA</strong> reconciles the fuel tax you owe across every state you drive through.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Gauge className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">ELD (Electronic Logging Device)</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Hardware wired into your truck that auto-records your Hours of Service — no daily paper logs. It must be a
            model on <strong>FMCSA's registered device list</strong>, and it keeps you out of the HOS violations that
            wreck a new carrier's CSA score.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Fuel className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">IFTA (fuel tax agreement)</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            A single <strong>quarterly report</strong> that splits the fuel tax you owe among the states you drove in,
            based on miles driven and fuel bought in each. You get an IFTA license and decals from your base state so no
            jurisdiction is shorted.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          The catch is <strong>record-keeping</strong>: IFTA is only as accurate as your miles-by-state and fuel
          receipts. Sloppy records mean surprise back-taxes at filing time, and an unregistered or malfunctioning ELD
          means automatic HOS violations.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Pick an ELD that also tracks IFTA miles by jurisdiction — it does double duty and makes your quarterly filing
          nearly automatic. And see the Paper Logs section below for your backup plan when the ELD goes down. Not legal
          or tax advice.
        </p>
      </div>
    </div>
  );
}
